import React, { useRef, useState } from 'react';
import { Upload, Image as ImageIcon, X, Layers } from 'lucide-react';

interface ImageUploaderProps {
  onFilesSelect: (files: File[]) => void;
  selectedCount: number;
  onClear: () => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onFilesSelect, selectedCount, onClear }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Cast Array.from result to File[] to avoid 'unknown' type errors when accessing file.type
      const files = (Array.from(e.dataTransfer.files) as File[]).filter(file => file.type.startsWith('image/'));
      if (files.length > 0) {
        onFilesSelect(files);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Cast Array.from result to File[] for consistency and type safety
      const files = Array.from(e.target.files) as File[];
      onFilesSelect(files);
    }
  };

  if (selectedCount > 0) {
    return (
      <div className="relative w-full aspect-video md:aspect-[4/3] rounded-xl overflow-hidden border-2 border-slate-700 group bg-slate-900 flex flex-col items-center justify-center">
        <div className="p-4 bg-slate-800 rounded-full mb-3 shadow-lg">
            <Layers size={32} className="text-blue-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-1">{selectedCount} Images Selected</h3>
        <p className="text-slate-400 text-sm">Ready to batch process</p>
        
        <button 
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="absolute top-4 right-4 p-2 bg-slate-800/80 hover:bg-red-500/80 text-white rounded-full transition-colors backdrop-blur-sm"
          >
            <X size={20} />
          </button>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative w-full aspect-video md:aspect-[4/3] rounded-xl border-2 border-dashed 
        flex flex-col items-center justify-center cursor-pointer transition-all duration-300
        ${isDragging 
          ? 'border-blue-500 bg-blue-500/5' 
          : 'border-slate-700 bg-slate-800/50 hover:bg-slate-800 hover:border-slate-600'
        }
      `}
    >
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        accept="image/*" 
        multiple
        className="hidden" 
      />
      
      <div className={`p-4 rounded-full bg-slate-800 mb-4 transition-transform ${isDragging ? 'scale-110' : ''}`}>
        {isDragging ? (
          <Upload size={32} className="text-blue-500" />
        ) : (
          <ImageIcon size={32} className="text-slate-400" />
        )}
      </div>
      
      <p className="text-lg font-medium text-slate-200 mb-1">
        {isDragging ? 'Drop images here' : 'Upload Images'}
      </p>
      <p className="text-sm text-slate-500 text-center px-4">
        Click or drag & drop to upload up to 10,000 images
      </p>
    </div>
  );
};